import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Edit, Trash2, Printer } from "react-feather";
import Select from "react-select";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import confirmDelete from "../components/confirmDelete";
import { fetchInvoices, deleteInvoiceThunk } from "../../core/redux/slices/invoiceSlice";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Draft", label: "Draft" },
  { value: "Paid", label: "Paid" },
  { value: "PartialPaid", label: "Partial Paid" },
  { value: "Cancelled", label: "Cancelled" },
];

const STATUS_BADGE = {
  Draft: "secondary",
  Paid: "success",
  PartialPaid: "warning",
  Cancelled: "danger",
};

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const InvoiceList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, loading } = useSelector((s) => s.invoices);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());

  useEffect(() => {
    dispatch(fetchInvoices());
  }, [dispatch]);

  const handleSearch = () => {
    dispatch(fetchInvoices());
  };

  // The /api/Invoice/all endpoint accepts no query params — date filtering is applied client-side.
  const data = useMemo(() => {
    const q = search.toLowerCase();
    return list.filter((inv) => {
      const matchSearch = !q || inv.patientName?.toLowerCase().includes(q) || inv.invoiceNumber?.toLowerCase().includes(q);
      const matchStatus = !statusFilter || inv.invoiceStatus === statusFilter;
      const d = inv.invoiceDate ? inv.invoiceDate.split("T")[0] : null;
      const matchDate = (!dateFrom || !d || d >= dateFrom) && (!dateTo || !d || d <= dateTo);
      return matchSearch && matchStatus && matchDate;
    });
  }, [list, search, statusFilter, dateFrom, dateTo]);

  const columns = [
    { title: "Invoice #", dataIndex: "invoiceNumber", width: 120 },
    { title: "Patient", dataIndex: "patientName", sorter: (a, b) => (a.patientName || "").localeCompare(b.patientName || "") },
    { title: "Date", dataIndex: "invoiceDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "Doctor", dataIndex: "doctorName" },
    {
      title: "Amount", dataIndex: "netTotal", align: "right", width: 120,
      render: fmt,
      sorter: (a, b) => (a.netTotal || 0) - (b.netTotal || 0),
    },
    {
      title: "Paid", dataIndex: "paidAmount", align: "right", width: 110,
      render: fmt,
    },
    {
      title: "Balance", dataIndex: "balanceAmount", align: "right", width: 110,
      render: (v) => <span className={v > 0 ? "text-danger fw-semibold" : "text-success"}>{fmt(v)}</span>,
    },
    {
      title: "Status", dataIndex: "invoiceStatus", width: 110,
      render: (v) => (
        <span className={`badge bg-${STATUS_BADGE[v] || "secondary"}`}>
          {v === "PartialPaid" ? "Partial Paid" : v}
        </span>
      ),
    },
    {
      title: "Action", dataIndex: "action", width: 120,
      render: (_, rec) => (
        <div className="edit-delete-action d-flex">
          <button className="me-2 p-2 btn btn-sm" title="Print" onClick={() => window.open(`/invoices/${rec.id}/print`, "_blank")}>
            <Printer size={15} />
          </button>
          <button className="me-2 p-2 btn btn-sm" title="View/Edit" onClick={() => navigate(`/invoices/${rec.id}/edit`)}>
            <Edit size={15} />
          </button>
          <button className="p-2 btn btn-sm text-danger"
            onClick={() => confirmDelete(() => dispatch(deleteInvoiceThunk(rec.id)), `invoice ${rec.invoiceNumber}`)}>
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Invoices" subtitle="Manage patient billing" onAdd={() => navigate("/invoices/new")} addLabel="New Invoice" />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top d-flex gap-2 flex-wrap align-items-center mb-2">
              <div className="d-flex align-items-center gap-1">
                <label className="mb-0 small">From</label>
                <input type="date" className="form-control form-control-sm" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)} style={{ width: 140 }} />
              </div>
              <div className="d-flex align-items-center gap-1">
                <label className="mb-0 small">To</label>
                <input type="date" className="form-control form-control-sm" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)} style={{ width: 140 }} />
              </div>
              <button className="btn btn-sm btn-primary" onClick={handleSearch}>Search</button>
              <div style={{ minWidth: 160 }}>
                <Select classNamePrefix="react-select" options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.find((o) => o.value && o.value === statusFilter) || null}
                  placeholder="All Statuses"
                  isClearable
                  onChange={(opt) => setStatusFilter(opt?.value || "")} />
              </div>
              <div className="search-input flex-grow-1">
                <input type="text" placeholder="Search patient or invoice#..."
                  className="form-control form-control-sm formsearch"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceList;
